import { useEffect, useState } from "react";
import { api, formatApiError, formatFCFA, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, RotateCw, FileMinus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "entrant", label: "Transfert entrant", icon: ArrowDownLeft, color: "emerald" },
  { value: "sortant", label: "Transfert sortant", icon: ArrowUpRight, color: "amber" },
  { value: "pret", label: "Prêt", icon: ArrowLeftRight, color: "blue" },
  { value: "renouvellement", label: "Renouvellement", icon: RotateCw, color: "slate" },
  { value: "fin_contrat", label: "Fin de contrat", icon: FileMinus, color: "red" },
];

const STATUS_BADGE = {
  en_attente: "bg-amber-100 text-amber-700 border-amber-200",
  valide: "bg-emerald-100 text-emerald-700 border-emerald-200",
  refuse: "bg-red-100 text-red-700 border-red-200",
};
const STATUS_LABELS = { en_attente: "En attente", valide: "Validé", refuse: "Refusé" };

const EMPTY = {
  player_name: "", type: "entrant", from_club: "", to_club: "AMAL VBC",
  date: new Date().toISOString().slice(0, 10), amount: 0, notes: "",
};

export default function Transferts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/transfers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/transfers", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Transfert créé");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/transfers/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce transfert ?")) return;
    await api.delete(`/transfers/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div data-testid="transferts-page">
      <PageHeader title="Gestion des transferts" subtitle="Mouvements de joueuses, prêts et fins de contrat">
        <Button onClick={() => setOpen(true)} data-testid="add-transfer-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouveau transfert
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full data-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                <th className="text-left p-4">Joueuse</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">De → Vers</th>
                <th className="text-left p-4">Date</th>
                <th className="text-right p-4">Montant</th>
                <th className="text-left p-4">Statut</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-slate-400">Aucun transfert enregistré.</td></tr>
              ) : items.map((t) => {
                const typ = TYPES.find((x) => x.value === t.type) || TYPES[0];
                const Icon = typ.icon;
                return (
                  <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-b border-slate-100">
                    <td className="p-4 font-semibold text-slate-900">{t.player_name}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Icon className="h-3.5 w-3.5 text-emerald-600" />
                        {typ.label}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      {t.from_club || "—"} → {t.to_club || "—"}
                    </td>
                    <td className="p-4 text-sm text-slate-600">{formatDate(t.date)}</td>
                    <td className="p-4 text-sm text-right text-slate-900 font-mono">
                      {t.amount ? formatFCFA(t.amount) : "—"}
                    </td>
                    <td className="p-4">
                      <Badge className={STATUS_BADGE[t.status]}>{STATUS_LABELS[t.status]}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1 justify-end items-center">
                        <Select value={t.status} onValueChange={(v) => updateStatus(t.id, v)}>
                          <SelectTrigger data-testid={`status-select-${t.id}`} className="h-8 w-32 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="valide">Valider</SelectItem>
                            <SelectItem value="refuse">Refuser</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={() => del(t.id)}
                          data-testid={`delete-transfer-${t.id}`}
                          className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">Nouveau transfert</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label="Nom de la joueuse" value={form.player_name}
                onChange={(v) => setForm({ ...form, player_name: v })} required testid="transfer-player" />
            </div>
            <SelectField label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })}
              options={TYPES.map((t) => ({ value: t.value, label: t.label }))} testid="transfer-type" />
            <Field label="Date" type="date" value={form.date}
              onChange={(v) => setForm({ ...form, date: v })} testid="transfer-date" />
            <Field label="Club d'origine" value={form.from_club}
              onChange={(v) => setForm({ ...form, from_club: v })} testid="transfer-from" />
            <Field label="Club destination" value={form.to_club}
              onChange={(v) => setForm({ ...form, to_club: v })} testid="transfer-to" />
            <div className="col-span-2">
              <Field label="Montant (FCFA)" type="number" value={form.amount}
                onChange={(v) => setForm({ ...form, amount: v })} testid="transfer-amount" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">
                Notes / Justificatifs
              </Label>
              <Textarea data-testid="transfer-notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-transfer-button" className="bg-emerald-600 hover:bg-emerald-700">
              Créer le dossier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
